import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Tabs } from "antd";
import { AuditOutlined } from "@ant-design/icons";

import { PortfolioPageShell } from "@/views/Portfolio/shared";
import { apiFetchJson } from "@/utils/apiFetch";
import DataMasteryHealth from "./DataMasteryHealth";
import MasteryActivity from "./MasteryActivity";
import MasteryGraph from "./MasteryGraph";
import MasteryIdentifiers from "./MasteryIdentifiers";
import MasteryNetworks from "./MasteryNetworks";
import MasteryOverview from "./MasteryOverview";
import MasteryScoreExplorer from "./MasteryScoreExplorer";
import type { MasteryQueueCounts } from "./DataMasteryQueue";

// Analytics-first Data Mastery: this page is the read-only analytics surface
// over the mastering output (per-run funnel, golden-layer health, scoring
// layer, graph, networks, identifier keyspace). Adjudication lives in the
// Review Queue — the stewards' own interface — behind the header button.

async function fetchCounts(): Promise<MasteryQueueCounts> {
  return apiFetchJson<MasteryQueueCounts>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/counts/`,
  );
}

export default function DataMasteryAnalytics() {
  const navigate = useNavigate();
  // ?view=graph&seed=… / &pair=a,b — how the review queue deep-links a record
  // into its match neighborhood (see DiffModal's "View in graph").
  const [params] = useSearchParams();
  const seed = params.get("seed") ?? undefined;
  const pair = params.get("pair") ?? undefined;
  const [view, setView] = useState(params.get("view") ?? "overview");

  const { data: counts } = useQuery({
    queryKey: ["mastery-queue-counts"],
    queryFn: fetchCounts,
    staleTime: 60_000,
  });

  return (
    <PortfolioPageShell
      title="Data Mastery"
      tooltipText="Analytics over the mastering output: run results, golden-layer health, scoring, networks, and identifiers."
      headerExtra={
        <Badge count={counts?.needs_review ?? 0} overflowCount={999}>
          <Button
            type="primary"
            icon={<AuditOutlined />}
            onClick={() => navigate("/portfolio/data/mastery/queue")}
          >
            Review Queue
          </Button>
        </Badge>
      }
    >
      {/* Sub-nav across the analytics views. destroyOnHidden keeps each
          view's queries from firing until it is opened. */}
      <Tabs
        activeKey={view}
        onChange={setView}
        destroyOnHidden
        items={[
          { key: "overview", label: "Overview", children: <MasteryOverview /> },
          {
            key: "health",
            label: "Data Health",
            children: <DataMasteryHealth />,
          },
          {
            key: "scores",
            label: "Score Explorer",
            children: <MasteryScoreExplorer />,
          },
          {
            key: "graph",
            label: "Graph",
            children: <MasteryGraph seed={seed} pair={pair} />,
          },
          { key: "networks", label: "Networks", children: <MasteryNetworks /> },
          {
            key: "activity",
            label: "Decision Log",
            children: <MasteryActivity />,
          },
          {
            key: "identifiers",
            label: "Identifiers",
            children: <MasteryIdentifiers />,
          },
        ]}
      />
    </PortfolioPageShell>
  );
}
